import { transition, trigger, useAnimation } from '@angular/animations'
import { Component, inject, Input, input, OnInit, output } from '@angular/core'
import {
  asPersonalId,
  CartDiscountType,
  CartState,
  Experiment,
  HardwareId,
  isThermometerKey,
  ProductKey,
  ProductType,
  RegistrationFlow,
  TargetAudience,
  thermometerKeys,
  WebSignupQueryParam,
} from '@naturalcycles/shared'
import { ThermComponent } from '@src/app/components/modals/therm/therm.component.js'
import { Decorate } from '@src/app/core/decorators/decorators.js'
import { AccountService } from '@src/app/core/services/account.service.js'
import { CartService } from '@src/app/core/services/cart.service.js'
import { DiscountCodeService } from '@src/app/core/services/discountCode.service.js'
import { ExperimentService } from '@src/app/core/services/experiment.service.js'
import { ModalService } from '@src/app/core/services/modal.service.js'
import { NavService } from '@src/app/core/services/nav.service.js'
import { SignalStore } from '@src/app/core/store/signalStore.js'
import { fadeAnimation } from '@src/app/core/util/animations.util.js'
import { ErrorHandlerType } from '@src/app/shared/typings/enum/error-handler.js'
import { ManageAccountPage, Page } from '@src/app/shared/typings/enum/pages.js'
import { LoaderType } from '@src/app/shared/typings/ui.js'

@Component({
  selector: 'app-discount-code',
  templateUrl: './discount-code.component.html',
  styleUrls: ['./discount-code.component.scss'],
  animations: [trigger('fadeAnimation', [transition('* => *', [useAnimation(fadeAnimation)])])],
  standalone: false,
})
export class DiscountCodeComponent implements OnInit {
  private cartService = inject(CartService)
  private modalService = inject(ModalService)
  private navService = inject(NavService)
  private accountService = inject(AccountService)
  private experimentService = inject(ExperimentService)
  private discountCodeService = inject(DiscountCodeService)

  private store = inject(SignalStore)

  protected ignoreConfirmation = input(false)
  protected forceShowConfirmation = input(false)

  @Input()
  code: string | null = null

  @Input()
  referral: string | null = null

  use = output<boolean>()

  protected ProductType = ProductType
  public ProductKey = ProductKey

  protected cart?: CartState
  protected includeTherm = false
  protected hasBluetoothTherm?: boolean

  ngOnInit(): void {
    this.cartService.emptyCart()
    if (this.code) {
      // Clear any previous discount code to make sure latest code is applied
      this.discountCodeService.deleteCurrentDiscountCodeMetadata()

      void this.apply()
    }
  }

  public submitCode(event: Event): void {
    event.preventDefault()
    void this.apply(true)
  }

  @Decorate({
    loaderType: LoaderType.BLOCKING,
    errorHandlerType: ErrorHandlerType.DIALOG,
  })
  public async apply(isManuallyApplied?: boolean): Promise<void> {
    const referral = this.referral || undefined
    const discountCode = await this.discountCodeService.persistDiscountCode({
      code: this.code!,
      referral,
    })
    const ignoreConfirmation = this.ignoreConfirmation()
    const forceShowConfirmation = this.forceShowConfirmation()

    if (this.code?.toLowerCase() === 'ouranctrial' && ignoreConfirmation) {
      await this.experimentService.logImpression(Experiment.WS_OURA_NO_IGNORED_CONFIRMATION)
    }

    const discountCodeType = discountCode?.type || CartDiscountType.CheckoutCode
    if (discountCodeType === CartDiscountType.PredefinedCart) {
      this.cart = await this.cartService.createPredefinedCart({
        code: this.code!,
        referrerId: referral ? asPersonalId(referral) : undefined,
      })
      this.hasBluetoothTherm = this.cart.items.some(i => isThermometerKey(i.key))
    }

    const isCbTestCode = discountCode.code === 'e2eCbTest100'
    const isTestBundleCode = discountCode.code === 'e2eTestBundle100'

    const isEncrypted = discountCode.encrypted || isCbTestCode || isTestBundleCode

    // If the code is encrypted (a.k.a pharmacy code) then we won't redirect to the goal page
    // We still want the pharmacy users to see the discount offer confirmation and press 'Start my journey'
    if (isEncrypted) {
      if (isCbTestCode) {
        return
      }
      const goal = await this.discountCodeService.getGoalFromDiscount(discountCode)
      await this.accountService.saveGoal(goal)

      return
    }

    const isOuraCode = discountCode.tags?.includes('ouraHwId')
    const isExistingOuraUser = discountCode.code.toLowerCase() === 'ouranctrial'

    // We show a nice confirmation button for predefined cart codes before they can navigate
    const shouldIgnoreConfirmation =
      (ignoreConfirmation && !forceShowConfirmation) || !!isManuallyApplied
    if (!shouldIgnoreConfirmation && discountCodeType === CartDiscountType.PredefinedCart) {
      return
    }

    if (discountCode.targetAudience === TargetAudience.activeOrChurned) {
      void this.navService.goToPage([ManageAccountPage.account, Page.login])
    } else if (isOuraCode && !isExistingOuraUser) {
      void this.navService.goToPage([Page.oura])
    } else if (isOuraCode && isExistingOuraUser) {
      await this.cartService.selectDevice(HardwareId.OURA, ProductKey.OURA_RING_DISCOUNT, true)
      void this.navService.goToPage([Page.measuringDevice], {
        queryParams: {
          [WebSignupQueryParam.flow]: RegistrationFlow.OURA.toString(),
        },
      })
    } else {
      void this.navService.goToPage([Page.measuringDevice])
    }
  }

  @Decorate({
    loaderType: LoaderType.BLOCKING,
    errorHandlerType: ErrorHandlerType.DIALOG,
  })
  public async continue(): Promise<void> {
    if (this.cart?.discount?.optionalThermometer && !this.includeTherm) {
      const discount = this.cartService.getDiscount()
      const items = this.cartService
        .getCartItemInputs()
        .filter(i => !thermometerKeys.includes(i.key))

      await this.cartService.update({
        hwId: this.store.$cart().hwId,
        items,
        discount,
      })
    }
    this.use.emit(true)
  }

  public toggleTherm(): void {
    this.includeTherm = !this.includeTherm
    if (!this.includeTherm) this.displayThermModal()
  }

  private displayThermModal(): void {
    void this.modalService.show({
      component: ThermComponent,
      modalName: 'ThermModal',
      skipControls: true,
      api: {
        select: (wantsTherm: boolean) => {
          void this.modalService.remove()
          this.includeTherm = wantsTherm
        },
      },
    })
  }
}
